/**
 * AI Capabilities - the site administrator's half of the AI decision (DR-029).
 *
 * One tab per feature, because the features are separate purchases from the
 * provider's side: an account can hold chat access and no image access, the
 * models are named differently, and they fail for different reasons. Every "AI
 * is broken" report used to start with nobody knowing which half was broken.
 *
 * The page is one switch and one diagnostic, in that order of importance:
 *
 *   - **Offering the feature** is the whole point of the screen, so it lives in
 *     the tab's header beside the feature name rather than in a card of its
 *     own. An earlier version gave it a numbered card identical to the two
 *     below it, which made the only decision on the page look like a third of
 *     the page.
 *   - **Checking a provider** is one job asked two ways - does *our*
 *     subscription key work, and would *this school's* key work - so it is one
 *     card with a source switch, not two near-identical forms. Nothing typed
 *     into the key form is ever stored.
 *
 * Deliberately absent: anything about a particular school. Which schools take a
 * feature is their choice and is shown on their own page.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, type SiteAiFeature } from '../../lib/api/client'
import { Button, Checkbox, Field, Input, Pill, Select, Spinner, useTabList } from '../../components/ui'
import { toast } from '../../components/toast'

/** Provider choices per feature kind. Beside the form that offers them rather
 *  than fetched: these are the adapters this build ships with, so they change
 *  when the code changes, not when the data does. */
const PROVIDERS: Record<string, string[]> = {
  text: ['openai'],
  image: ['openai', 'gemini'],
}

type Source = 'subscription' | 'byok'

export function AiCapabilities() {
  const tabs = useTabList()
  const features = useQuery({ queryKey: ['platform', 'ai-features'], queryFn: api.siteAiFeatures })
  const [active, setActive] = useState<string | null>(null)

  if (features.isLoading) return <Spinner label="Loading AI capabilities" />
  const list = features.data ?? []
  if (list.length === 0) return <p className="tc-ai-help">This build registers no AI features.</p>

  // Falls back to the first entry rather than storing an index, so a feature
  // added or withdrawn server-side cannot leave the page pointing at nothing.
  const current = list.find((feature) => feature.key === active) ?? list[0]

  return (
    <div className="tc-ai-capabilities">
      <div className="tc-segmented tc-ai-feature-menu" ref={tabs.ref} onKeyDown={tabs.onKeyDown} role="tablist" aria-label="AI features">
        {list.map((feature) => (
          <button
            key={feature.key}
            role="tab"
            id={`tc-ai-cap-${feature.key}-tab`}
            aria-controls={`tc-ai-cap-${feature.key}-panel`}
            aria-selected={feature.key === current.key}
            className={`tc-segment ${feature.key === current.key ? 'is-active' : ''}`}
            onClick={() => setActive(feature.key)}
          >
            {feature.label}
            {/* The word, not a colour. A grey dot reads as "unselected tab" as
                readily as "not offered", and a legend for two states is a
                legend too many. Nothing is shown when a feature is offered,
                because that is the ordinary state. */}
            {!feature.enabled && <span className="tc-segment-note">Off</span>}
          </button>
        ))}
      </div>

      <div
        className="tc-ai-feature-content"
        role="tabpanel"
        id={`tc-ai-cap-${current.key}-panel`}
        aria-labelledby={`tc-ai-cap-${current.key}-tab`}
      >
        <FeaturePanel key={current.key} feature={current} />
      </div>
    </div>
  )
}

function FeaturePanel({ feature }: { feature: SiteAiFeature }) {
  const queryClient = useQueryClient()
  const headingId = `tc-ai-cap-${feature.key}-heading`

  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => api.setSiteAiFeature(feature.key, enabled),
    onSuccess: (result) => {
      queryClient.setQueryData(['platform', 'ai-features'], result)
      // Every school's panel folds this catalogue into its own switches, so
      // their cached copies are now wrong.
      queryClient.invalidateQueries({ queryKey: ['platform', 'ai'] })
      queryClient.invalidateQueries({ queryKey: ['ai-billing'] })
      toast(
        result.find((item) => item.key === feature.key)?.enabled
          ? `${feature.label} is now offered to schools.`
          : `${feature.label} has been withdrawn from every school.`,
      )
    },
    onError: (error) =>
      toast(error instanceof ApiError ? error.message : 'That could not be saved.', 'error'),
  })

  return (
    <section className="tc-ai-cap-panel" aria-labelledby={headingId}>
      {/* The switch sits in the heading because it is the decision this screen
          exists for - everything below is a diagnostic. */}
      <header className="tc-ai-cap-head">
        <div>
          <h2 id={headingId}>{feature.label}</h2>
          <p className="tc-ai-help">{feature.description}</p>
        </div>
        <Checkbox
          label="Offer to schools"
          checked={feature.enabled}
          disabled={setEnabled.isPending}
          onChange={(event) => setEnabled.mutate(event.target.checked)}
        />
      </header>

      {!feature.enabled && (
        <p className="tc-ai-cap-withdrawn" role="status">
          Withdrawn. No school can use {feature.label.toLowerCase()} while this is off - the
          switch disappears from their settings rather than sitting there doing nothing. You can
          still test a provider below before offering it.
        </p>
      )}

      <Diagnostics feature={feature} />
    </section>
  )
}

/**
 * One card, one question: does a provider answer for this feature?
 *
 * Asked of the deployment's own key, or of a key a school is considering. Two
 * separate cards said the same thing twice and made the page look like it had
 * more settings than it has.
 */
function Diagnostics({ feature }: { feature: SiteAiFeature }) {
  const choices = PROVIDERS[feature.kind] ?? PROVIDERS.text
  const [source, setSource] = useState<Source>('subscription')
  const [provider, setProvider] = useState(choices[0])
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [model, setModel] = useState('')
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null)

  const run = useMutation({
    mutationFn: () =>
      api.testSiteAiFeature(
        feature.key,
        source === 'subscription'
          ? { mode: 'subscription' }
          : {
              mode: 'byok',
              provider,
              api_key: apiKey,
              base_url: baseUrl || null,
              model: model || null,
            },
      ),
    onSuccess: (response) =>
      setResult({
        ok: response.ok,
        text: response.ok ? `Connected in ${response.latency_ms ?? 0} ms.` : response.message,
      }),
    onError: (error) =>
      setResult({ ok: false, text: error instanceof Error ? error.message : 'The test failed.' }),
  })

  const choose = (next: Source) => {
    setSource(next)
    // A result belongs to the source that produced it; leaving it on screen
    // after the switch would attribute one provider's answer to another.
    setResult(null)
  }

  return (
    <div className="tc-ai-section">
      <header className="tc-ai-section-head">
        <div>
          <h3>Check a provider</h3>
          <p className="tc-ai-help">
            Makes one real call. It touches no school's allowance, and a key entered here is used
            for that call and never stored.
          </p>
        </div>
      </header>

      <div className="tc-ai-section-body">
        <div className="tc-segmented tc-segmented-sm" role="group" aria-label="What to test">
          <button
            type="button"
            className={`tc-segment ${source === 'subscription' ? 'is-active' : ''}`}
            aria-pressed={source === 'subscription'}
            onClick={() => choose('subscription')}
          >
            Our subscription key
          </button>
          <button
            type="button"
            className={`tc-segment ${source === 'byok' ? 'is-active' : ''}`}
            aria-pressed={source === 'byok'}
            onClick={() => choose('byok')}
          >
            A school's own key
          </button>
        </div>

        {source === 'subscription' ? (
          <dl className="tc-ai-cap-facts">
            <div>
              <dt>Provider</dt>
              <dd>{feature.platform_provider ?? 'Not configured'}</dd>
            </div>
            <div>
              <dt>Model</dt>
              <dd>{feature.platform_model ?? '—'}</dd>
            </div>
            <div>
              <dt>Key</dt>
              <dd>
                <Pill tone={feature.platform_key_configured ? 'success' : 'warning'}>
                  {feature.platform_key_configured ? 'Configured' : 'Missing'}
                </Pill>
              </dd>
            </div>
          </dl>
        ) : (
          /* Dashed, like the per-school key box: it marks fields holding a
             credential that is not part of the saved settings. */
          <div className="tc-ai-key">
            <div className="tc-ai-key-fields">
              <Field label="Provider">
                <Select value={provider} onChange={(event) => setProvider(event.target.value)}>
                  {choices.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Model" hint="Blank uses the provider's default.">
                <Input value={model} onChange={(event) => setModel(event.target.value)} />
              </Field>
              <Field label="API key" hint="Used for this one call, then discarded.">
                <Input
                  type="password"
                  autoComplete="off"
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                />
              </Field>
              <Field label="Endpoint" hint="Blank for the public API. Set for Azure or a gateway.">
                <Input value={baseUrl} onChange={(event) => setBaseUrl(event.target.value)} />
              </Field>
            </div>
          </div>
        )}

        <div className="tc-ai-actions">
          <Button
            variant="secondary"
            disabled={run.isPending || (source === 'byok' && !apiKey.trim())}
            onClick={() => run.mutate()}
          >
            {run.isPending ? 'Testing…' : 'Test the connection'}
          </Button>
        </div>

        {/* `role="status"` rather than `alert`: a failed diagnostic is an
            answer, not an emergency. */}
        {result && (
          <p className={`tc-ai-cap-result ${result.ok ? 'is-ok' : 'is-bad'}`} role="status">
            {result.text}
          </p>
        )}
      </div>
    </div>
  )
}
